import { Stack } from "@mui/material";
import { Fragment } from "react";

import type { Status } from "./StepIcon";

import { cn } from "../utils/styling";
import { ICON_SIZE_CLASS, InlineStepIconAndLabel, StepIconAndLabel } from "./StepIcon";

export const StepperLine = () => {
  return <div className="h-1 w-16 border-b-ds-subtle" />;
};

export type LDSStepperProps = {
  className?: string;
  currentStep: number;
  onCompletedStepClick?: (stepNumber: number) => void;
  steps: { name: string; title: string }[];
  variant?: "inline" | "stacked";
};

export type StepperVariant = "inline" | "stacked";

export function getStepState(stepNumber: number, currentStep: number): Status {
  if (stepNumber < currentStep) return "done";
  if (stepNumber === currentStep) return "active";
  return "disabled";
}

const getStepIconProps = (index: number, currentStep: number, onCompletedStepClick?: (stepNumber: number) => void) => {
  const stepNumber = index + 1;
  const state = getStepState(stepNumber, currentStep);
  const isCircleClickable = state === "done" && Boolean(onCompletedStepClick);

  const onClick = isCircleClickable ? () => onCompletedStepClick?.(stepNumber) : undefined;
  return { onClick, status: state, stepNumber };
};

export const Stepper = ({
  className,
  currentStep,
  onCompletedStepClick,
  steps,
  variant = "stacked",
}: LDSStepperProps) => {
  if (variant === "inline") {
    return (
      <Stack className={cn("gap-8", className)}>
        {steps.map((step, index) => {
          const { onClick, status, stepNumber } = getStepIconProps(index, currentStep, onCompletedStepClick);
          return (
            <Fragment key={step.name}>
              <InlineStepIconAndLabel label={step.title} onClick={onClick} status={status} stepNumber={stepNumber} />
              {index < steps.length - 1 ? <StepperLine /> : null}
            </Fragment>
          );
        })}
      </Stack>
    );
  }
  return (
    <Stack className={className}>
      {steps.map((step, index) => {
        const { onClick, status, stepNumber } = getStepIconProps(index, currentStep, onCompletedStepClick);
        return (
          <Stack className="items-start gap-0" key={step.name}>
            <StepIconAndLabel label={step.title} onClick={onClick} status={status} stepNumber={stepNumber} />
            {index < steps.length - 1 ? (
              <Stack className={cn(ICON_SIZE_CLASS, "justify-center")}>
                <StepperLine />
              </Stack>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
};
